<template>
  <div class="model-comparison">
    <!-- Header -->
    <div class="comparison-header">
      <h4 class="mb-0">
        <i class="fa fa-chart-line me-2"></i>Model Comparison
      </h4>
      <div class="header-actions">
        <button class="btn btn-sm btn-outline-secondary" @click="showHelpModal = true">
          <i class="fa fa-question-circle"></i>
        </button>
      </div>
    </div>

    <!-- Controls Bar -->
    <div class="controls-bar">
      <!-- Location Picker -->
      <div class="control-group location-group">
        <label class="control-label">Location</label>
        <div class="location-input-wrapper">
          <input 
            type="text" 
            class="form-control form-control-sm location-input"
            v-model="locationText"
            @keyup.enter="geocodeLocation"
            placeholder="Enter location..."
          />
          <button class="btn btn-sm btn-outline-primary" @click="geocodeLocation" :disabled="isGeocoding">
            <i class="fa" :class="isGeocoding ? 'fa-spinner fa-spin' : 'fa-search'"></i>
          </button>
          <button class="btn btn-sm btn-outline-secondary" @click="useCurrentLocation" :disabled="isGeocoding">
            <i class="fa fa-location-crosshairs"></i>
          </button>
        </div>
        <div v-if="location" class="location-display">
          <i class="fa fa-map-marker-alt"></i>
          {{ location.name || `${location.lat?.toFixed(4)}, ${location.lon?.toFixed(4)}` }}
        </div>
      </div>

      <!-- Date Range -->
      <div class="control-group">
        <label class="control-label">Date Range</label>
        <div class="date-inputs">
          <input type="date" class="form-control form-control-sm" v-model="startDate" />
          <span class="date-separator">to</span>
          <input type="date" class="form-control form-control-sm" v-model="endDate" />
        </div>
        <div class="quick-dates">
          <button class="btn btn-xs btn-outline-secondary" @click="setDateRange('today')">Today</button>
          <button class="btn btn-xs btn-outline-secondary" @click="setDateRange('3days')">3 Days</button>
          <button class="btn btn-xs btn-outline-secondary" @click="setDateRange('week')">Week</button>
          <button class="btn btn-xs btn-outline-secondary" @click="setDateRange('2weeks')">2 Weeks</button>
        </div>
      </div>

      <!-- Time Resolution -->
      <div class="control-group">
        <label class="control-label">Resolution</label>
        <div class="btn-group btn-group-sm">
          <button 
            class="btn" 
            :class="timeResolution === 'hours' ? 'btn-primary' : 'btn-outline-secondary'"
            @click="timeResolution = 'hours'"
          >Hourly</button>
          <button 
            class="btn" 
            :class="timeResolution === 'days' ? 'btn-primary' : 'btn-outline-secondary'"
            @click="timeResolution = 'days'"
          >Daily</button>
        </div>
      </div>

      <!-- Fetch Button -->
      <div class="control-group">
        <button 
          class="btn btn-primary btn-sm fetch-btn"
          @click="fetchData"
          :disabled="!canFetch || isLoading"
        >
          <i class="fa" :class="isLoading ? 'fa-spinner fa-spin' : 'fa-download'"></i>
          {{ isLoading ? 'Loading...' : 'Compare Models' }}
        </button>
        <div class="api-key-status" v-if="hasValidApiKey">
          <i class="fa fa-key text-success"></i>
          <span v-if="hasSession">Session key</span>
          <span v-else>API key set</span>
        </div>
        <div class="api-key-status text-danger" v-else>
          <i class="fa fa-exclamation-triangle"></i>
          <span>No API key</span>
        </div>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="comparison-content">
      <!-- Model Selection Panel (Left) -->
      <div class="model-panel" :class="{ collapsed: modelPanelCollapsed }">
        <div class="panel-header" @click="modelPanelCollapsed = !modelPanelCollapsed">
          <span>Models</span>
          <span class="selected-count">{{ selectedModelCount }}/{{ totalModelCount }}</span>
          <i class="fa" :class="modelPanelCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'"></i>
        </div>
        
        <div class="panel-content" v-show="!modelPanelCollapsed">
          <!-- Quick Presets -->
          <div class="model-presets">
            <button class="btn btn-xs btn-outline-primary" @click="selectPreset('all')">All</button>
            <button class="btn btn-xs btn-outline-primary" @click="selectPreset('none')">None</button>
            <button class="btn btn-xs btn-outline-success" @click="selectPreset('v2')">
              <i class="fa fa-bolt"></i> V2 Only
            </button>
            <button class="btn btn-xs btn-outline-secondary" @click="selectPreset('legacy')">
              <i class="fa fa-history"></i> Legacy Only
            </button>
          </div>

          <!-- V2 Models Loading indicator -->
          <div v-if="v2ModelsLoading" class="loading-models">
            <i class="fa fa-spinner fa-spin"></i> Loading V2 models from API...
          </div>

          <!-- Model Groups -->
          <div class="model-groups">
            <!-- Default Model -->
            <div class="model-group">
              <div class="group-models" style="padding-top: 8px;">
                <label class="model-checkbox">
                  <input type="checkbox" v-model="selectedModels" value="default" />
                  <span class="model-color" style="background-color: #000000;"></span>
                  <span class="model-name">Default (no model specified)</span>
                </label>
              </div>
            </div>

            <!-- V2 Models (from API) -->
            <div class="model-group">
              <div class="group-header v2-header" @click="toggleGroup('v2')">
                <i class="fa fa-bolt me-1 text-success"></i>
                <span>V2 Models (New)</span>
                <span class="group-count">{{ modelGroups.v2.length }}</span>
                <i class="fa ms-auto" :class="expandedGroups.v2 ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
              </div>
              <div class="group-models" v-show="expandedGroups.v2">
                <div v-if="!v2ModelsLoaded && !v2ModelsLoading" class="no-models-msg">
                  <i class="fa fa-info-circle"></i> Click "Fetch Data" to load V2 models
                </div>
                <label v-for="model in modelGroups.v2" :key="model.id" class="model-checkbox">
                  <input type="checkbox" v-model="selectedModels" :value="model.id" />
                  <span class="model-color" :style="{ backgroundColor: model.color }"></span>
                  <span class="model-name">{{ model.name }}</span>
                  <span class="model-badge badge-v2">V2</span>
                </label>
              </div>
            </div>

            <!-- Legacy Models -->
            <div class="model-group">
              <div class="group-header legacy-header" @click="toggleGroup('legacy')">
                <i class="fa fa-history me-1 text-warning"></i>
                <span>Legacy Models</span>
                <span class="group-count">{{ modelGroups.legacy.length }}</span>
                <i class="fa ms-auto" :class="expandedGroups.legacy ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
              </div>
              <div class="group-models" v-show="expandedGroups.legacy">
                <label v-for="model in modelGroups.legacy" :key="model.id" class="model-checkbox">
                  <input type="checkbox" v-model="selectedModels" :value="model.id" />
                  <span class="model-color" :style="{ backgroundColor: model.color }"></span>
                  <span class="model-name">{{ model.name }}</span>
                  <span class="model-badge badge-legacy">Legacy</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Chart & Results Area (Center) -->
      <div class="results-area">
        <!-- Element Selector -->
        <div class="element-selector">
          <label class="control-label">Element:</label>
          <select class="form-select form-select-sm" v-model="selectedElement">
            <option v-for="el in availableElements" :key="el.id" :value="el.id">
              {{ el.name }}
            </option>
          </select>
          
          <label class="control-label ms-3">Baseline:</label>
          <select class="form-select form-select-sm" v-model="baselineModel">
            <option value="">None</option>
            <option v-for="model in selectedModelConfigs" :key="model.id" :value="model.id">
              {{ model.name }}{{ hasV2Version(model.id) ? ' ⚠️ Legacy' : '' }}{{ isV2Model(model.id) ? ' ✓ V2' : '' }}
            </option>
          </select>

          <div class="view-toggle ms-auto">
            <div class="btn-group btn-group-sm">
              <button 
                class="btn" 
                :class="viewMode === 'chart' ? 'btn-primary' : 'btn-outline-secondary'"
                @click="viewMode = 'chart'"
              ><i class="fa fa-chart-line"></i></button>
              <button 
                class="btn" 
                :class="viewMode === 'table' ? 'btn-primary' : 'btn-outline-secondary'"
                @click="viewMode = 'table'"
              ><i class="fa fa-table"></i></button>
              <button 
                class="btn" 
                :class="viewMode === 'diff' ? 'btn-primary' : 'btn-outline-secondary'"
                @click="viewMode = 'diff'"
                :disabled="!baselineModel"
              ><i class="fa fa-code-compare"></i></button>
            </div>
          </div>
        </div>

        <!-- Chart View -->
        <div class="chart-container" v-show="viewMode === 'chart' || viewMode === 'diff'">
          <div class="comparison-chart" ref="chartContainer">
            <svg ref="chartSvg"></svg>
            <div class="chart-tooltip" ref="chartTooltip">
              <div class="tooltip-content"></div>
            </div>
          </div>
          
          <!-- Chart Legend -->
          <div class="chart-legend" v-if="hasData">
            <div 
              v-for="model in selectedModelConfigs" 
              :key="model.id" 
              class="legend-item"
              :class="{ 
                dimmed: highlightedModel && highlightedModel !== model.id,
                'is-ensemble': model.isEnsemble 
              }"
              @mouseenter="highlightModel(model.id)"
              @mouseleave="highlightModel(null)"
            >
              <span class="legend-color" :style="{ backgroundColor: model.color }"></span>
              <span class="legend-name">{{ model.name }}</span>
              <span class="legend-legacy" v-if="hasV2Version(model.id)" title="Legacy - V2 version available">⚠️</span>
              <span class="legend-v2" v-if="isV2Model(model.id)" title="V2 Model">✓</span>
              <span class="legend-spread-indicator" v-if="model.isEnsemble && showEnsembleSpread">
                <i class="fa fa-chart-area"></i>
              </span>
            </div>
          </div>
        </div>

        <!-- Table View -->
        <div class="table-container" v-show="viewMode === 'table'">
          <table class="table table-sm table-striped table-hover">
            <thead>
              <tr>
                <th class="sticky-col">Time</th>
                <th 
                  v-for="model in selectedModelConfigs" 
                  :key="model.id"
                  :style="{ borderTop: `3px solid ${model.color}` }"
                  :class="{ 'legacy-model': hasV2Version(model.id), 'v2-model': isV2Model(model.id) }"
                >
                  {{ model.name }}
                  <span v-if="hasV2Version(model.id)" class="th-legacy" title="Legacy - V2 version available">⚠️</span>
                  <span v-if="isV2Model(model.id)" class="th-v2" title="V2 Model">✓</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in tableData" :key="row.datetime">
                <td class="sticky-col">{{ formatDateTime(row.datetime) }}</td>
                <td 
                  v-for="model in selectedModelConfigs" 
                  :key="model.id"
                  :class="getCellClass(row, model.id)"
                >
                  {{ formatValue(row[model.id]) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty State -->
        <div class="empty-state" v-if="!hasData && !isLoading">
          <i class="fa fa-chart-area fa-3x mb-3"></i>
          <h5>No Data Yet</h5>
          <p>Select a location, date range, and models, then click "Compare Models" to fetch data.</p>
        </div>

        <!-- Loading State -->
        <div class="loading-state" v-if="isLoading">
          <i class="fa fa-spinner fa-spin fa-3x mb-3"></i>
          <h5>Loading Model Data...</h5>
          <p>Fetching from {{ selectedModels.length }} models</p>
          <div class="progress">
            <div class="progress-bar progress-bar-striped progress-bar-animated" :style="{ width: loadingProgress + '%' }"></div>
          </div>
        </div>
      </div>

      <!-- Stats Panel (Right) -->
      <div class="stats-panel" :class="{ collapsed: statsPanelCollapsed }">
        <div class="panel-header" @click="statsPanelCollapsed = !statsPanelCollapsed">
          <span>Statistics</span>
          <i class="fa" :class="statsPanelCollapsed ? 'fa-chevron-left' : 'fa-chevron-down'"></i>
        </div>
        
        <div class="panel-content" v-show="!statsPanelCollapsed && hasData">
          <!-- Stats Table -->
          <table class="stats-table">
            <thead>
              <tr>
                <th>Model</th>
                <th>Min</th>
                <th>Max</th>
                <th>Avg</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="stat in modelStats" 
                :key="stat.id"
                :class="{ 'is-baseline': stat.id === baselineModel }"
              >
                <td>
                  <span class="stat-color" :style="{ backgroundColor: stat.color }"></span>
                  {{ stat.name }}
                </td>
                <td :class="{ 'is-min': stat.isOverallMin }">{{ formatValue(stat.min) }}</td>
                <td :class="{ 'is-max': stat.isOverallMax }">{{ formatValue(stat.max) }}</td>
                <td>{{ formatValue(stat.avg) }}</td>
              </tr>
            </tbody>
          </table>

          <!-- Spread Analysis -->
          <div class="spread-analysis" v-if="spreadStats">
            <h6>Model Spread</h6>
            <div class="spread-item">
              <span>Range:</span>
              <strong>{{ formatValue(spreadStats.range) }}</strong>
            </div>
            <div class="spread-item">
              <span>Std Dev:</span>
              <strong>{{ formatValue(spreadStats.stdDev) }}</strong>
            </div>
            <div class="spread-item">
              <span>Agreement:</span>
              <strong :class="spreadStats.agreementClass">{{ spreadStats.agreement }}</strong>
            </div>
          </div>

          <!-- Model Rankings -->
          <div class="model-rankings" v-if="baselineModel && modelRankings.length">
            <h6>Deviation from {{ getModelName(baselineModel) }}{{ hasV2Version(baselineModel) ? ' ⚠️' : '' }}</h6>
            <div 
              v-for="rank in modelRankings" 
              :key="rank.id" 
              class="ranking-item"
            >
              <span class="rank-color" :style="{ backgroundColor: rank.color }"></span>
              <span class="rank-name">{{ rank.name }}</span>
              <span class="rank-legacy" v-if="hasV2Version(rank.id)">⚠️</span>
              <span class="rank-v2" v-if="isV2Model(rank.id)">✓</span>
              <span class="rank-deviation" :class="rank.deviationClass">
                {{ rank.deviationText }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Help Modal -->
    <div class="modal fade" :class="{ show: showHelpModal }" v-if="showHelpModal" @click.self="showHelpModal = false">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Model Comparison Help</h5>
            <button type="button" class="btn-close" @click="showHelpModal = false"></button>
          </div>
          <div class="modal-body">
            <h6>Available Models</h6>
            <p>Compare forecasts from multiple weather models including:</p>
            <ul>
              <li><strong>Blended Results</strong> - Our optimized combined forecast</li>
              <li><strong>US Regional</strong> - HRRR, NAM, NDFD (higher resolution for US)</li>
              <li><strong>European</strong> - ECMWF, ICON-EU, ICON-D2, AROME, ARPEGE</li>
              <li><strong>Global</strong> - GFS, ICON Global, GDPS, UK Met Office</li>
              <li><strong>AI/ML</strong> - AI-powered models like ECMWF AIFS and AI GFS</li>
              <li><strong>Ensemble Models</strong> - GFS Ensemble (gfes_2) with 31 members showing forecast uncertainty</li>
            </ul>
            
            <h6>Ensemble Spread Visualization</h6>
            <p>When selecting ensemble models like <strong>GFS Ensemble (Spread)</strong>:</p>
            <ul>
              <li>A shaded area shows the range between minimum and maximum ensemble member values</li>
              <li>The center line shows the ensemble mean</li>
              <li>Toggle "Show Spread Range" in the Ensemble Models group to enable/disable</li>
              <li>Adjust opacity slider to change the spread area visibility</li>
              <li>Hover tooltips show both the mean and the full spread range</li>
            </ul>
            
            <h6>Comparison Views</h6>
            <ul>
              <li><strong>Chart</strong> - Line chart showing all selected models</li>
              <li><strong>Table</strong> - Tabular data with all values</li>
              <li><strong>Difference</strong> - Shows deviation from selected baseline</li>
            </ul>
            
            <h6>Tips</h6>
            <ul>
              <li>Hover over the chart to see values at specific times</li>
              <li>Click legend items to highlight individual models</li>
              <li>Use presets to quickly select model groups</li>
              <li>Select a baseline model to see relative differences</li>
              <li>Use the "Ensemble" preset to quickly compare ensemble forecasts</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { loadModelsFromApi } from '../data/weatherMapLayers.js';

// Legacy models - these use the old forecastDataset parameter
// Note: Legacy models use prefixed IDs (legacy_xxx) to avoid conflicts with V2 models
// The apiModelId property stores the actual value to send to the API
const LEGACY_MODELS = [
  { id: 'default', name: 'Default', color: '#000000', group: 'blended', width: 3, noForecastDataset: true, isLegacy: false },
  { id: 'legacy_result', apiModelId: 'result', name: 'Result (Legacy)', color: '#000000', group: 'legacy', width: 4, isLegacy: true },
  { id: 'legacy_hrrr', apiModelId: 'hrrr', name: 'HRRR (Legacy)', color: '#0000FF', group: 'legacy', isLegacy: true },
  { id: 'legacy_nam', apiModelId: 'nam', name: 'NAM (Legacy)', color: '#FFFF00', group: 'legacy', isLegacy: true },
  { id: 'legacy_ndfd', apiModelId: 'ndfd', name: 'NDFD (Legacy)', color: '#FF00FF', group: 'legacy', isLegacy: true },
  { id: 'legacy_gfs', apiModelId: 'gfs', name: 'GFS (Legacy)', color: '#FF0000', group: 'legacy', isLegacy: true },
  { id: 'legacy_gfes', apiModelId: 'gfes', name: 'GFS Ensemble (Legacy)', color: '#CFCFCF', group: 'legacy', isLegacy: true },
  { id: 'legacy_usblended', apiModelId: 'usblended', name: 'US Blended (Legacy)', color: '#FFAAFF', group: 'legacy', isLegacy: true },
  { id: 'legacy_vcusblended', apiModelId: 'vcusblended', name: 'VC US Blended (Legacy)', color: '#00FFFF', group: 'legacy', width: 2, isLegacy: true },
  { id: 'legacy_icond2', apiModelId: 'icond2', name: 'ICON-D2 (Legacy)', color: '#AAFFAA', group: 'legacy', isLegacy: true },
  { id: 'legacy_iconeu', apiModelId: 'iconeu', name: 'ICON-EU (Legacy)', color: '#00FFFF', group: 'legacy', isLegacy: true },
  { id: 'legacy_ecmwf', apiModelId: 'ecmwf', name: 'ECMWF (Legacy)', color: '#00FF00', group: 'legacy', isLegacy: true },
  { id: 'legacy_iconglobal', apiModelId: 'iconglobal', name: 'ICON Global (Legacy)', color: '#FFAAFF', group: 'legacy', isLegacy: true },
  { id: 'legacy_gdps', apiModelId: 'gdps', name: 'GDPS (Legacy)', color: '#FFFFAA', group: 'legacy', isLegacy: true },
  { id: 'legacy_ukmo_global', apiModelId: 'ukmo_global', name: 'UK Met Global (Legacy)', color: '#FFFFAA', group: 'legacy', isLegacy: true },
];

// V2 model color palette (assigned dynamically when loaded from API)
const V2_COLORS = [
  '#2196F3', '#4CAF50', '#FF9800', '#9C27B0', '#00BCD4',
  '#E91E63', '#3F51B5', '#009688', '#FFC107', '#795548',
  '#607D8B', '#8BC34A', '#CDDC39', '#FF5722', '#673AB7',
  '#03A9F4', '#4DB6AC', '#FFB74D', '#BA68C8', '#81D4FA'
];

// Weather elements with display names
const ELEMENTS = [
  { id: 'datetime', name: 'Date/Time', hidden: true },
  { id: 'datetimeEpoch', name: 'Epoch', hidden: true },
  { id: 'icon', name: 'Icon', hidden: true },
  { id: 'temp', name: 'Temperature' },
  { id: 'feelslike', name: 'Feels Like' },
  { id: 'humidity', name: 'Humidity' },
  { id: 'dew', name: 'Dew Point' },
  { id: 'precip', name: 'Precipitation' },
  { id: 'precipprob', name: 'Precip Probability' },
  { id: 'snow', name: 'Snow' },
  { id: 'snowdepth', name: 'Snow Depth' },
  { id: 'windspeed', name: 'Wind Speed' },
  { id: 'windgust', name: 'Wind Gust' },
  { id: 'winddir', name: 'Wind Direction' },
  { id: 'pressure', name: 'Pressure' },
  { id: 'visibility', name: 'Visibility' },
  { id: 'cloudcover', name: 'Cloud Cover' },
  { id: 'uvindex', name: 'UV Index' },
  { id: 'solarradiation', name: 'Solar Radiation' },
  { id: 'solarenergy', name: 'Solar Energy' },
];

export default {
  name: 'ModelComparison',
  
  props: {
    apiKey: {
      type: String,
      default: ''
    },
    initialLocation: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      // Session/Auth State (populated from window.vcCommunicator)
      sessionId: null,
      sessionState: null,
      apiKeyOverride: '', // User-provided API key override
      
      // Location - default to New York
      location: { lat: 40.7128, lon: -74.006, name: 'New York, NY' },
      locationText: 'New York, NY',
      isGeocoding: false,
      
      // Date range
      startDate: this.getDefaultStartDate(),
      endDate: this.getDefaultEndDate(),
      
      // Options
      timeResolution: 'hours',
      selectedElement: 'temp',
      baselineModel: 'default',
      viewMode: 'chart',
      
      // Model selection
      selectedModels: ['default'],
      expandedGroups: {
        v2: true,
        legacy: false
      },
      
      // V2 Models loaded from API
      v2Models: [],
      v2ModelsLoading: false,
      v2ModelsLoaded: false,
      
      // Ensemble spread options
      showEnsembleSpread: true,
      ensembleSpreadOpacity: 0.3,
      modelPanelCollapsed: false,
      statsPanelCollapsed: false,
      
      // Data
      queryData: null,
      isLoading: false,
      loadingProgress: 0,
      
      // UI state
      showHelpModal: false,
      highlightedModel: null,
    };
  },

  computed: {
    // Combine legacy models and V2 models loaded from API
    allModels() {
      return [...LEGACY_MODELS, ...this.v2Models];
    },
    
    modelGroups() {
      const groups = {
        v2: [],
        legacy: []
      };
      
      // Add V2 models
      this.v2Models.forEach(model => {
        groups.v2.push(model);
      });
      
      // Add legacy models
      LEGACY_MODELS.forEach(model => {
        if (model.isLegacy) {
          groups.legacy.push(model);
        }
      });
      
      return groups;
    },
    
    hasEnsembleModel() {
      return this.selectedModels.some(id => {
        const model = this.allModels.find(m => m.id === id);
        return model && model.isEnsemble;
      });
    },
    
    selectedModelCount() {
      return this.selectedModels.length;
    },
    
    totalModelCount() {
      return this.allModels.length;
    },
    
    selectedModelConfigs() {
      return this.selectedModels.map(id => 
        this.allModels.find(m => m.id === id)
      ).filter(Boolean);
    },
    
    availableElements() {
      return ELEMENTS.filter(el => !el.hidden);
    },
    
    /**
     * Get the effective API key to use for API requests
     * Priority: 1) Prop apiKey, 2) User override, 3) Session key, 4) window.vcApiKey
     */
    weatherApiKey() {
      // First check prop
      if (this.apiKey && this.apiKey.trim()) {
        return this.apiKey.trim();
      }
      // Then user override
      if (this.apiKeyOverride && this.apiKeyOverride.trim()) {
        return this.apiKeyOverride.trim();
      }
      // Then session state
      if (this.sessionState?.apiKey) {
        return this.sessionState.apiKey;
      }
      // Finally window global
      return window.vcApiKey || '';
    },
    
    hasValidApiKey() {
      return !!this.weatherApiKey;
    },
    
    hasSession() {
      return !!this.sessionState?.apiKey;
    },
    
    canFetch() {
      return this.location && this.selectedModels.length > 0 && this.startDate && this.endDate && this.hasValidApiKey;
    },
    
    hasData() {
      return this.queryData && Object.keys(this.queryData).length > 0;
    },
    
    tableData() {
      if (!this.hasData) return [];
      
      const data = [];
      const element = this.selectedElement;
      
      // Collect all unique datetimes
      const datetimes = new Set();
      this.selectedModels.forEach(modelId => {
        const modelData = this.queryData[modelId];
        if (modelData && modelData.days) {
          modelData.days.forEach(day => {
            if (this.timeResolution === 'hours' && day.hours) {
              day.hours.forEach(hour => datetimes.add(hour.datetimeEpoch));
            } else {
              datetimes.add(day.datetimeEpoch);
            }
          });
        }
      });
      
      // Sort datetimes
      const sortedDatetimes = Array.from(datetimes).sort((a, b) => a - b);
      
      // Build rows
      sortedDatetimes.forEach(dt => {
        const row = { datetime: dt };
        
        this.selectedModels.forEach(modelId => {
          const modelData = this.queryData[modelId];
          if (modelData && modelData.days) {
            const value = this.getValueAtTime(modelData, dt, element);
            row[modelId] = value;
          }
        });
        
        data.push(row);
      });
      
      return data;
    },
    
    modelStats() {
      if (!this.hasData) return [];
      
      const element = this.selectedElement;
      const stats = [];
      
      let overallMin = Infinity;
      let overallMax = -Infinity;
      let minModelId = null;
      let maxModelId = null;
      
      this.selectedModels.forEach(modelId => {
        const modelData = this.queryData[modelId];
        if (!modelData || !modelData.days) return;
        
        const values = this.extractValues(modelData, element);
        if (values.length === 0) return;
        
        const min = Math.min(...values);
        const max = Math.max(...values);
        const avg = values.reduce((a, b) => a + b, 0) / values.length;
        
        if (min < overallMin) {
          overallMin = min;
          minModelId = modelId;
        }
        if (max > overallMax) {
          overallMax = max;
          maxModelId = modelId;
        }
        
        const model = this.allModels.find(m => m.id === modelId);
        stats.push({
          id: modelId,
          name: model?.name || modelId,
          color: model?.color || '#888',
          min,
          max,
          avg
        });
      });
      
      // Mark overall min/max
      stats.forEach(s => {
        s.isOverallMin = s.id === minModelId;
        s.isOverallMax = s.id === maxModelId;
      });
      
      return stats;
    },
    
    spreadStats() {
      if (this.modelStats.length < 2) return null;
      
      const avgs = this.modelStats.map(s => s.avg);
      const min = Math.min(...avgs);
      const max = Math.max(...avgs);
      const range = max - min;
      
      const mean = avgs.reduce((a, b) => a + b, 0) / avgs.length;
      const variance = avgs.reduce((a, v) => a + Math.pow(v - mean, 2), 0) / avgs.length;
      const stdDev = Math.sqrt(variance);
      
      let agreement = 'Low';
      let agreementClass = 'text-danger';
      
      if (stdDev < 2) {
        agreement = 'High';
        agreementClass = 'text-success';
      } else if (stdDev < 5) {
        agreement = 'Moderate';
        agreementClass = 'text-warning';
      }
      
      return { range, stdDev, agreement, agreementClass };
    },
    
    modelRankings() {
      if (!this.baselineModel || !this.hasData) return [];
      
      const baselineStat = this.modelStats.find(s => s.id === this.baselineModel);
      if (!baselineStat) return [];
      
      return this.modelStats
        .filter(s => s.id !== this.baselineModel)
        .map(s => {
          const deviation = s.avg - baselineStat.avg;
          const model = this.allModels.find(m => m.id === s.id);
          
          return {
            id: s.id,
            name: model?.name || s.id,
            color: model?.color || '#888',
            deviation,
            deviationText: deviation >= 0 ? `+${this.formatValue(deviation)}` : this.formatValue(deviation),
            deviationClass: deviation >= 0 ? 'text-danger' : 'text-primary'
          };
        })
        .sort((a, b) => Math.abs(b.deviation) - Math.abs(a.deviation));
    }
  },

  methods: {
    // Date helpers
    getDefaultStartDate() {
      const d = new Date();
      return d.toISOString().split('T')[0];
    },
    
    getDefaultEndDate() {
      const d = new Date();
      d.setDate(d.getDate() + 7);
      return d.toISOString().split('T')[0];
    },
    
    setDateRange(preset) {
      const today = new Date();
      this.startDate = today.toISOString().split('T')[0];
      
      const end = new Date(today);
      switch (preset) {
        case 'today':
          break;
        case '3days':
          end.setDate(end.getDate() + 3);
          break;
        case 'week':
          end.setDate(end.getDate() + 7);
          break;
        case '2weeks':
          end.setDate(end.getDate() + 14);
          break;
      }
      this.endDate = end.toISOString().split('T')[0];
    },
    
    // Location
    async geocodeLocation() {
      if (!this.locationText.trim()) return;
      
      this.isGeocoding = true;
      try {
        // Use VC geocoding API or browser geolocation
        const vcCommunicator = window.vcCommunicator;
        if (vcCommunicator && vcCommunicator.geocode) {
          const result = await vcCommunicator.geocode(this.locationText);
          if (result && result.length > 0) {
            this.location = {
              lat: result[0].latitude,
              lon: result[0].longitude,
              name: result[0].resolvedAddress || this.locationText
            };
          }
        } else {
          // Fallback: treat as coordinates if it looks like lat,lon
          const match = this.locationText.match(/^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/);
          if (match) {
            this.location = {
              lat: parseFloat(match[1]),
              lon: parseFloat(match[2]),
              name: this.locationText
            };
          } else {
            this.location = {
              lat: 40.7128,
              lon: -74.006,
              name: this.locationText
            };
          }
        }
      } catch (err) {
        console.error('Geocoding error:', err);
      } finally {
        this.isGeocoding = false;
      }
    },
    
    useCurrentLocation() {
      if (!navigator.geolocation) return;
      
      this.isGeocoding = true;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.location = {
            lat: pos.coords.latitude,
            lon: pos.coords.longitude,
            name: 'Current Location'
          };
          this.locationText = `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`;
          this.isGeocoding = false;
        },
        (err) => {
          console.error('Geolocation error:', err);
          this.isGeocoding = false;
        }
      );
    },
    
    // Model selection
    toggleGroup(group) {
      this.expandedGroups[group] = !this.expandedGroups[group];
    },
    
    selectPreset(preset) {
      switch (preset) {
        case 'all':
          this.selectedModels = this.allModels.map(m => m.id);
          break;
        case 'none':
          this.selectedModels = [];
          break;
        case 'v2':
          // Select only V2 models (loaded from API)
          this.selectedModels = this.v2Models.map(m => m.id);
          this.expandedGroups.v2 = true;
          break;
        case 'legacy':
          // Select only legacy models
          this.selectedModels = LEGACY_MODELS.filter(m => m.isLegacy).map(m => m.id);
          this.expandedGroups.legacy = true;
          break;
      }
    },
    
    getModelName(id) {
      const model = this.allModels.find(m => m.id === id);
      return model?.name || id;
    },
    
    // Get model display name with legacy indicator
    getModelDisplayName(id) {
      const model = this.allModels.find(m => m.id === id);
      const name = model?.name || id;
      if (model && model.isLegacy) {
        return name + ' ⚠️';  // Legacy indicator
      }
      return name;
    },
    
    // Check if a model is a V2 model (loaded from API, not legacy)
    isV2Model(modelId) {
      const model = this.allModels.find(m => m.id === modelId);
      return model && !model.isLegacy && model.id !== 'default';
    },
    
    // Check if this is a legacy model
    isLegacyModel(modelId) {
      const model = this.allModels.find(m => m.id === modelId);
      return model && model.isLegacy;
    },
    
    // Legacy compatibility - no longer needed but kept for template references
    hasV2Version(modelId) {
      return false;  // Simplified - legacy/V2 now determined by isLegacy flag
    },
    
    // Get the base model name (without _2 suffix)
    getBaseModelId(modelId) {
      if (modelId.endsWith('_2')) {
        return modelId.slice(0, -2);
      }
      return modelId;
    },
    
    // Load V2 models from API
    async loadV2Models() {
      if (this.v2ModelsLoaded || this.v2ModelsLoading) return;
      
      const apiKey = this.weatherApiKey;
      if (!apiKey) {
        console.log('No API key, skipping V2 models load');
        return;
      }
      
      this.v2ModelsLoading = true;
      
      try {
        const result = await loadModelsFromApi('https://weather.visualcrossing.com', apiKey);
        
        if (result.fromApi && result.quickSelectModels) {
          // Convert API models to our format
          // V2 models use the model ID with _2 suffix for the forecastDataset parameter
          this.v2Models = result.quickSelectModels.map((modelId, index) => ({
            id: modelId,  // Internal ID for component (e.g., 'hrrr')
            apiModelId: `${modelId}_2`,  // API value for forecastDataset (e.g., 'hrrr_2')
            name: result.modelNames[modelId] || modelId,
            color: V2_COLORS[index % V2_COLORS.length],
            group: 'v2',
            isLegacy: false,
            isV2: true
          }));
          
          console.log(`Loaded ${this.v2Models.length} V2 models from API`);
        }
        
        this.v2ModelsLoaded = true;
      } catch (err) {
        console.error('Failed to load V2 models:', err);
      } finally {
        this.v2ModelsLoading = false;
      }
    },
    
    // Data fetching
    async fetchData() {
      if (!this.canFetch) return;
      
      this.isLoading = true;
      this.loadingProgress = 0;
      this.queryData = {};
      
      const vcCommunicator = window.vcCommunicator;
      const models = [...this.selectedModels];
      const totalModels = models.length;
      
      try {
        for (let i = 0; i < models.length; i++) {
          const modelId = models[i];
          this.loadingProgress = ((i + 1) / totalModels) * 100;
          
          const locationStr = `${this.location.lat},${this.location.lon}`;
          const include = this.timeResolution === 'hours' ? 'hours' : 'days';
          
          // Build query definition object for vcCommunicator
          const queryDefn = {
            startdate: this.startDate,
            enddate: this.endDate,
            include: include,
            elements: ELEMENTS.map(e => e.id).join(','),
            options: 'usefcst'
          };
          
          // Add model/forecastDataset unless model has noForecastDataset flag
          const modelConfig = this.allModels.find(m => m.id === modelId);
          if (modelId && !(modelConfig && modelConfig.noForecastDataset)) {
            // Use apiModelId if available (for legacy models), otherwise use the id directly
            queryDefn.forecastDataset = modelConfig?.apiModelId || modelId;
          }
          
          try {
            let data;
            if (vcCommunicator && vcCommunicator.retrieveTimelineData) {
              // vcCommunicator.retrieveTimelineData(defn, location, name, contentType, requireKey, forceGlobalKey)
              data = await vcCommunicator.retrieveTimelineData(queryDefn, locationStr, null, "json", true, false);
            } else {
              // Fallback to direct API call
              // Use apiModelId for legacy models, otherwise use the id
              const apiModel = modelConfig?.apiModelId || modelId;
              const options = {
                location: locationStr,
                startdate: this.startDate,
                enddate: this.endDate,
                include: include,
                options: `usefcst,models:${apiModel}`,
                elements: ELEMENTS.map(e => e.id).join(',')
              };
              data = await this.fetchFromApi(options);
            }
            
            if (data) {
              this.queryData[modelId] = data;
            }
          } catch (modelErr) {
            console.warn(`Failed to fetch ${modelId}:`, modelErr);
          }
        }
        
        this.$nextTick(() => {
          this.renderChart();
        });
      } catch (err) {
        console.error('Data fetch error:', err);
      } finally {
        this.isLoading = false;
      }
    },
    
    async fetchFromApi(options) {
      const apiKey = this.weatherApiKey;
      if (!apiKey) {
        console.error('No API key available');
        return null;
      }
      
      const baseUrl = 'https://weather.visualcrossing.com/VisualCrossingWebServices/rest/services/timeline';
      const url = `${baseUrl}/${encodeURIComponent(options.location)}/${options.startdate}/${options.enddate}?key=${apiKey}&include=${options.include}&options=${options.options}&elements=${options.elements}`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      return response.json();
    },
    
    // Data extraction
    getValueAtTime(modelData, datetimeEpoch, element) {
      if (!modelData.days) return null;
      
      for (const day of modelData.days) {
        if (this.timeResolution === 'hours' && day.hours) {
          const hour = day.hours.find(h => h.datetimeEpoch === datetimeEpoch);
          if (hour && hour[element] !== undefined) return hour[element];
        } else if (day.datetimeEpoch === datetimeEpoch) {
          return day[element];
        }
      }
      return null;
    },
    
    extractValues(modelData, element) {
      const values = [];
      if (!modelData.days) return values;
      
      modelData.days.forEach(day => {
        if (this.timeResolution === 'hours' && day.hours) {
          day.hours.forEach(hour => {
            if (hour[element] !== undefined && hour[element] !== null) {
              values.push(hour[element]);
            }
          });
        } else if (day[element] !== undefined && day[element] !== null) {
          values.push(day[element]);
        }
      });
      
      return values;
    },
    
    // Formatting
    formatValue(val) {
      if (val === null || val === undefined) return '-';
      return typeof val === 'number' ? val.toFixed(1) : val;
    },
    
    formatDateTime(epoch) {
      const d = new Date(epoch * 1000);
      if (this.timeResolution === 'hours') {
        return d.toLocaleString(undefined, { 
          month: 'short', 
          day: 'numeric', 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      }
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    },
    
    getCellClass(row, modelId) {
      if (!this.baselineModel || modelId === this.baselineModel) return '';
      
      const baseVal = row[this.baselineModel];
      const val = row[modelId];
      
      if (baseVal === null || val === null) return '';
      
      const diff = val - baseVal;
      if (Math.abs(diff) < 1) return 'cell-similar';
      return diff > 0 ? 'cell-higher' : 'cell-lower';
    },
    
    highlightModel(modelId) {
      this.highlightedModel = modelId;
      // Could also update chart highlighting here
    },
    
    // Chart rendering
    renderChart() {
      if (!this.hasData || !this.$refs.chartSvg) return;
      
      const container = this.$refs.chartContainer;
      const svg = d3.select(this.$refs.chartSvg);
      const tooltip = d3.select(this.$refs.chartTooltip);
      
      // Clear previous
      svg.selectAll('*').remove();
      
      // Dimensions
      const margin = { top: 20, right: 30, bottom: 40, left: 50 };
      const width = container.clientWidth - margin.left - margin.right;
      const height = 350 - margin.top - margin.bottom;
      
      svg.attr('width', width + margin.left + margin.right)
         .attr('height', height + margin.top + margin.bottom);
      
      const g = svg.append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);
      
      // Prepare data
      const element = this.selectedElement;
      const allData = [];
      
      this.selectedModels.forEach(modelId => {
        const modelData = this.queryData[modelId];
        if (!modelData || !modelData.days) return;
        
        const model = this.allModels.find(m => m.id === modelId);
        const points = [];
        const spreadPoints = []; // For ensemble spread (min/max range)
        const isEnsembleModel = model && model.isEnsemble;
        
        modelData.days.forEach(day => {
          if (this.timeResolution === 'hours' && day.hours) {
            day.hours.forEach(hour => {
              if (hour[element] !== undefined && hour[element] !== null) {
                const baseValue = this.viewMode === 'diff' && this.baselineModel 
                  ? this.getBaselineValue(hour.datetimeEpoch, element) || 0
                  : 0;
                
                points.push({
                  datetime: hour.datetimeEpoch * 1000,
                  value: hour[element] - baseValue
                });
                
                // Collect ensemble spread data if available
                if (isEnsembleModel && this.showEnsembleSpread) {
                  const minKey = element + 'min';
                  const maxKey = element + 'max';
                  const minVal = hour[minKey] !== undefined ? hour[minKey] : hour[element];
                  const maxVal = hour[maxKey] !== undefined ? hour[maxKey] : hour[element];
                  
                  spreadPoints.push({
                    datetime: hour.datetimeEpoch * 1000,
                    min: minVal - baseValue,
                    max: maxVal - baseValue,
                    value: hour[element] - baseValue
                  });
                }
              }
            });
          } else if (day[element] !== undefined && day[element] !== null) {
            const baseValue = this.viewMode === 'diff' && this.baselineModel
              ? this.getBaselineValue(day.datetimeEpoch, element) || 0
              : 0;
            
            points.push({
              datetime: day.datetimeEpoch * 1000,
              value: day[element] - baseValue
            });
            
            // Collect ensemble spread data if available
            if (isEnsembleModel && this.showEnsembleSpread) {
              const minKey = element + 'min';
              const maxKey = element + 'max';
              const minVal = day[minKey] !== undefined ? day[minKey] : day[element];
              const maxVal = day[maxKey] !== undefined ? day[maxKey] : day[element];
              
              spreadPoints.push({
                datetime: day.datetimeEpoch * 1000,
                min: minVal - baseValue,
                max: maxVal - baseValue,
                value: day[element] - baseValue
              });
            }
          }
        });
        
        if (points.length > 0) {
          allData.push({
            modelId,
            model,
            points,
            spreadPoints: isEnsembleModel ? spreadPoints : null,
            isEnsemble: isEnsembleModel
          });
        }
      });
      
      if (allData.length === 0) return;
      
      // Scales - include ensemble spread in extent calculation
      const xExtent = d3.extent(allData.flatMap(d => d.points.map(p => p.datetime)));
      let yValues = allData.flatMap(d => d.points.map(p => p.value));
      
      // Include ensemble spread min/max in y extent
      allData.forEach(d => {
        if (d.spreadPoints && d.spreadPoints.length > 0) {
          d.spreadPoints.forEach(sp => {
            yValues.push(sp.min, sp.max);
          });
        }
      });
      
      const yExtent = d3.extent(yValues);
      const yPadding = (yExtent[1] - yExtent[0]) * 0.1;
      
      const x = d3.scaleTime()
        .domain(xExtent)
        .range([0, width]);
      
      const y = d3.scaleLinear()
        .domain([yExtent[0] - yPadding, yExtent[1] + yPadding])
        .range([height, 0]);
      
      // Zero line for diff mode
      if (this.viewMode === 'diff') {
        g.append('line')
          .attr('x1', 0)
          .attr('x2', width)
          .attr('y1', y(0))
          .attr('y2', y(0))
          .attr('stroke', '#999')
          .attr('stroke-dasharray', '5,5');
      }
      
      // Ensemble spread areas (render first, so lines are on top)
      const area = d3.area()
        .defined(d => d.min !== null && d.max !== null)
        .x(d => x(d.datetime))
        .y0(d => y(d.min))
        .y1(d => y(d.max))
        .curve(d3.curveMonotoneX);
      
      allData.forEach(data => {
        if (data.isEnsemble && data.spreadPoints && data.spreadPoints.length > 0 && this.showEnsembleSpread) {
          g.append('path')
            .datum(data.spreadPoints)
            .attr('class', `ensemble-spread ensemble-${data.modelId}`)
            .attr('fill', data.model?.color || '#888')
            .attr('fill-opacity', this.ensembleSpreadOpacity)
            .attr('stroke', 'none')
            .attr('d', area);
          
          // Add a label for the spread
          const lastPoint = data.spreadPoints[data.spreadPoints.length - 1];
          if (lastPoint) {
            g.append('text')
              .attr('class', 'ensemble-label')
              .attr('x', x(lastPoint.datetime) + 5)
              .attr('y', y((lastPoint.min + lastPoint.max) / 2))
              .attr('fill', data.model?.color || '#888')
              .attr('font-size', '10px')
              .attr('dominant-baseline', 'middle')
              .text(`±${((lastPoint.max - lastPoint.min) / 2).toFixed(1)}`);
          }
        }
      });
      
      // Lines
      const line = d3.line()
        .defined(d => d.value !== null)
        .x(d => x(d.datetime))
        .y(d => y(d.value))
        .curve(d3.curveMonotoneX);
      
      allData.forEach(data => {
        g.append('path')
          .datum(data.points)
          .attr('class', `model-line model-${data.modelId}`)
          .attr('fill', 'none')
          .attr('stroke', data.model?.color || '#888')
          .attr('stroke-width', data.isEnsemble ? 2.5 : (data.model?.width || 1.5))
          .attr('d', line);
      });
      
      // Axes
      g.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).tickFormat(d3.timeFormat('%m/%d %H:%M')))
        .selectAll('text')
        .attr('transform', 'rotate(-45)')
        .style('text-anchor', 'end');
      
      g.append('g')
        .call(d3.axisLeft(y).ticks(6));
      
      // Tooltip interaction
      const me = this;
      const focus = g.append('line')
        .attr('class', 'focus-line')
        .attr('y1', 0)
        .attr('y2', height)
        .attr('stroke', '#999')
        .attr('stroke-dasharray', '3,3')
        .style('opacity', 0);
      
      svg.on('mousemove', function(event) {
        const [mx] = d3.pointer(event);
        let xVal = x.invert(mx - margin.left);
        
        // Snap to nearest time interval based on resolution
        if (me.timeResolution === 'hours') {
          const hourMs = 60 * 60 * 1000;
          xVal = new Date(Math.round(xVal.getTime() / hourMs) * hourMs);
        } else {
          // Daily - snap to nearest day (midnight)
          const dayMs = 24 * 60 * 60 * 1000;
          xVal = new Date(Math.round(xVal.getTime() / dayMs) * dayMs);
        }
        
        // Update focus line position to snapped time
        const snappedX = x(xVal);
        focus.attr('x1', snappedX)
             .attr('x2', snappedX)
             .style('opacity', 1);
        
        // Build tooltip content - use appropriate format for resolution
        const timeFormat = me.timeResolution === 'hours' ? '%b %d, %H:%M' : '%b %d';
        let html = `<div class="tooltip-time">${d3.timeFormat(timeFormat)(xVal)}</div>`;
        
        // Determine time threshold - if closest point is more than 1.5 intervals away, show dash
        // Use 90 minutes (5400000ms) for hourly data as threshold
        const timeThreshold = me.timeResolution === 'hours' ? 90 * 60 * 1000 : 36 * 60 * 60 * 1000;
        
        allData.forEach(data => {
          if (!data.points || data.points.length === 0) {
            // No data for this model
            html += `<div class="tooltip-row">
              <span class="tooltip-color" style="background:${data.model?.color || '#888'}"></span>
              <span class="tooltip-name">${data.model?.name || data.modelId}</span>
              <span class="tooltip-value">—</span>
            </div>`;
            return;
          }
          
          const closestPoint = data.points.reduce((a, p) => 
            Math.abs(p.datetime - xVal) < Math.abs(a.datetime - xVal) ? p : a
          );
          
          // Check if the closest point is within the time threshold
          // If the hovered time is past the model's data range, show dash
          const timeDiff = Math.abs(closestPoint.datetime - xVal);
          const isOutOfRange = timeDiff > timeThreshold;
          
          // Check for ensemble spread data
          let spreadInfo = '';
          if (!isOutOfRange && data.isEnsemble && data.spreadPoints) {
            const closestSpread = data.spreadPoints.reduce((a, p) => 
              Math.abs(p.datetime - xVal) < Math.abs(a.datetime - xVal) ? p : a
            );
            if (closestSpread && closestSpread.min !== undefined && closestSpread.max !== undefined) {
              spreadInfo = `<span class="tooltip-spread">(${me.formatValue(closestSpread.min)} - ${me.formatValue(closestSpread.max)})</span>`;
            }
          }
          
          const ensembleIcon = data.isEnsemble ? '<i class="fa fa-chart-area" style="font-size:9px;margin-right:2px;"></i>' : '';
          const displayValue = isOutOfRange ? '—' : `${me.formatValue(closestPoint.value)} ${spreadInfo}`;
          
          html += `<div class="tooltip-row ${data.isEnsemble ? 'is-ensemble' : ''}">
            <span class="tooltip-color" style="background:${data.model?.color || '#888'}"></span>
            <span class="tooltip-name">${ensembleIcon}${data.model?.name || data.modelId}</span>
            <span class="tooltip-value">${displayValue}</span>
          </div>`;
        });
        
        tooltip.select('.tooltip-content').html(html);
        tooltip.style('opacity', 1)
               .style('left', `${mx + 10}px`)
               .style('top', '20px');
      });
      
      svg.on('mouseleave', function() {
        focus.style('opacity', 0);
        tooltip.style('opacity', 0);
      });
    },
    
    getBaselineValue(datetimeEpoch, element) {
      if (!this.baselineModel || !this.queryData[this.baselineModel]) return null;
      return this.getValueAtTime(this.queryData[this.baselineModel], datetimeEpoch, element);
    }
  },

  watch: {
    queryData: {
      handler() {
        this.$nextTick(() => this.renderChart());
      },
      deep: true
    },
    selectedElement() {
      this.$nextTick(() => this.renderChart());
    },
    viewMode() {
      this.$nextTick(() => this.renderChart());
    },
    baselineModel() {
      this.$nextTick(() => this.renderChart());
    },
    showEnsembleSpread() {
      this.$nextTick(() => this.renderChart());
    },
    ensembleSpreadOpacity() {
      this.$nextTick(() => this.renderChart());
    },
    initialLocation: {
      handler(val) {
        if (val) {
          this.location = val;
          this.locationText = val.name || `${val.lat}, ${val.lon}`;
        }
      },
      immediate: true
    }
  },

  mounted() {
    window.addEventListener('resize', this.renderChart);
    
    // Initialize session state from vcCommunicator
    const vcCommunicator = window.vcCommunicator;
    if (vcCommunicator) {
      this.sessionId = vcCommunicator.sessionId;
      this.sessionState = vcCommunicator.sessionState;
      
      // Listen for session creation
      vcCommunicator.addHandler('createsession', (sessionId, sessionState) => {
        this.sessionId = sessionId;
        this.sessionState = sessionState;
        // Load V2 models when session is available
        this.loadV2Models();
      });
      
      // Listen for session termination
      vcCommunicator.addHandler('terminatesession', () => {
        this.sessionId = null;
        this.sessionState = null;
      });
      
      // Load V2 models if we already have an API key
      if (this.weatherApiKey) {
        this.loadV2Models();
      }
    }
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.renderChart);
  }
};
</script>

<style scoped>
.model-comparison {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f8f9fa;
  font-size: 13px;
}

/* Header */
.comparison-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: linear-gradient(135deg, #1a365d 0%, #2d4a7c 100%);
  color: white;
}

.comparison-header h4 {
  font-size: 16px;
  font-weight: 600;
}

.header-actions .btn {
  color: white;
  border-color: rgba(255,255,255,0.3);
}

/* Controls Bar */
.controls-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 12px 16px;
  background: white;
  border-bottom: 1px solid #dee2e6;
  align-items: flex-end;
}

.control-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.control-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #6c757d;
}

.api-key-status {
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
}

.api-key-status .fa-key {
  font-size: 10px;
}

.location-input-wrapper {
  display: flex;
  gap: 4px;
}

.location-input {
  min-width: 200px;
}

.location-display {
  font-size: 11px;
  color: #28a745;
}

.date-inputs {
  display: flex;
  align-items: center;
  gap: 6px;
}

.date-separator {
  color: #6c757d;
}

.quick-dates {
  display: flex;
  gap: 4px;
  margin-top: 4px;
}

.btn-xs {
  padding: 2px 6px;
  font-size: 11px;
}

.fetch-btn {
  min-width: 130px;
}

/* Main Content */
.comparison-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* Model Panel */
.model-panel {
  width: 220px;
  background: white;
  border-right: 1px solid #dee2e6;
  display: flex;
  flex-direction: column;
  transition: width 0.2s;
}

.model-panel.collapsed {
  width: 40px;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #f1f3f5;
  border-bottom: 1px solid #dee2e6;
  cursor: pointer;
  font-weight: 600;
}

.selected-count {
  font-size: 11px;
  color: #6c757d;
  margin-right: auto;
  margin-left: 8px;
}

.panel-content {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.model-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #dee2e6;
}

.model-group {
  margin-bottom: 8px;
}

.group-header {
  display: flex;
  align-items: center;
  padding: 6px 8px;
  background: #e9ecef;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  font-size: 12px;
}

.group-header:hover {
  background: #dee2e6;
}

.group-models {
  padding: 6px 0 6px 16px;
}

.model-checkbox {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
  cursor: pointer;
  font-size: 12px;
}

.model-checkbox input {
  margin: 0;
}

.model-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;
}

.model-badge {
  font-size: 9px;
  padding: 1px 4px;
  background: #17a2b8;
  color: white;
  border-radius: 3px;
  margin-left: auto;
}

.badge-v2 {
  background: #28a745;
  font-weight: 600;
}

.badge-legacy {
  background: #dc3545;
  font-weight: normal;
}

/* V2/Legacy group headers */
.v2-header {
  background: rgba(40, 167, 69, 0.1);
  border-left: 3px solid #28a745;
}

.legacy-header {
  background: rgba(220, 53, 69, 0.1);
  border-left: 3px solid #dc3545;
}

.group-count {
  font-size: 11px;
  color: #6c757d;
  margin-left: 4px;
}

.loading-models {
  padding: 12px;
  text-align: center;
  color: #6c757d;
  font-size: 12px;
}

.no-models-msg {
  padding: 8px 12px;
  color: #6c757d;
  font-size: 11px;
  font-style: italic;
}

/* Legacy/V2 indicators in various places */
.legend-legacy, .th-legacy, .rank-legacy {
  color: #dc3545;
  font-size: 12px;
  margin-left: 2px;
}

.legend-v2, .th-v2, .rank-v2 {
  color: #28a745;
  font-size: 11px;
  font-weight: 600;
  margin-left: 2px;
}

.legacy-model {
  background: rgba(220, 53, 69, 0.1) !important;
}

.v2-model {
  background: rgba(40, 167, 69, 0.1) !important;
}

/* Results Area */
.results-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: white;
}

.element-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-bottom: 1px solid #dee2e6;
  flex-wrap: wrap;
}

.element-selector .form-select {
  width: auto;
  min-width: 150px;
}

.view-toggle {
  margin-left: auto;
}

/* Chart */
.chart-container {
  flex: 1;
  padding: 16px;
  overflow: hidden;
}

.comparison-chart {
  position: relative;
  width: 100%;
  height: 350px;
  background: #fff;
}

.comparison-chart svg {
  width: 100%;
  height: 100%;
}

.chart-tooltip {
  position: absolute;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s;
  z-index: 100;
  min-width: 150px;
}

.tooltip-content :deep(.tooltip-time) {
  font-weight: 600;
  margin-bottom: 6px;
  padding-bottom: 6px;
  border-bottom: 1px solid #eee;
}

.tooltip-content :deep(.tooltip-row) {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
}

.tooltip-content :deep(.tooltip-color) {
  width: 10px;
  height: 10px;
  border-radius: 2px;
}

.tooltip-content :deep(.tooltip-value) {
  margin-left: auto;
  font-weight: 600;
}

.chart-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 8px 16px;
  border-top: 1px solid #eee;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: opacity 0.2s;
}

.legend-item.dimmed {
  opacity: 0.3;
}

.legend-color {
  width: 16px;
  height: 3px;
  border-radius: 1px;
}

.legend-spread-indicator {
  font-size: 10px;
  color: #fd7e14;
  margin-left: 2px;
}

.legend-item.is-ensemble .legend-color {
  height: 8px;
  opacity: 0.7;
}

/* Table */
.table-container {
  flex: 1;
  overflow: auto;
  padding: 16px;
}

.table-container .table {
  font-size: 12px;
}

.sticky-col {
  position: sticky;
  left: 0;
  background: white;
  z-index: 1;
}

.cell-similar {
  background: rgba(40, 167, 69, 0.1) !important;
}

.cell-higher {
  background: rgba(220, 53, 69, 0.1) !important;
}

.cell-lower {
  background: rgba(0, 123, 255, 0.1) !important;
}

/* Empty/Loading States */
.empty-state,
.loading-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  text-align: center;
  padding: 40px;
}

.empty-state i,
.loading-state i {
  color: #adb5bd;
}

.loading-state .progress {
  width: 200px;
  height: 8px;
}

/* Stats Panel */
.stats-panel {
  width: 250px;
  background: white;
  border-left: 1px solid #dee2e6;
  display: flex;
  flex-direction: column;
  transition: width 0.2s;
}

.stats-panel.collapsed {
  width: 40px;
}

.stats-table {
  width: 100%;
  font-size: 11px;
  border-collapse: collapse;
}

.stats-table th,
.stats-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #eee;
}

.stats-table th {
  text-align: left;
  font-weight: 600;
  background: #f8f9fa;
}

.stats-table .is-baseline {
  background: rgba(0, 123, 255, 0.1);
}

.stats-table .is-min {
  color: #007bff;
  font-weight: 600;
}

.stats-table .is-max {
  color: #dc3545;
  font-weight: 600;
}

.stat-color {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  margin-right: 4px;
  vertical-align: middle;
}

.spread-analysis {
  padding: 12px;
  margin: 12px 8px;
  background: #f8f9fa;
  border-radius: 4px;
}

.spread-analysis h6 {
  font-size: 12px;
  margin-bottom: 8px;
}

.spread-item {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 12px;
}

.model-rankings {
  padding: 12px;
  margin: 12px 8px;
  background: #f8f9fa;
  border-radius: 4px;
}

.model-rankings h6 {
  font-size: 12px;
  margin-bottom: 8px;
}

.ranking-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
  font-size: 12px;
}

.rank-color {
  width: 8px;
  height: 8px;
  border-radius: 2px;
}

.rank-name {
  flex: 1;
}

.rank-deviation {
  font-weight: 600;
}

/* Modal */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal.show {
  display: flex;
}

.modal-dialog {
  max-width: 500px;
  width: 90%;
}

.modal-content {
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.modal-body {
  padding: 16px;
  max-height: 60vh;
  overflow-y: auto;
}

.modal-body h6 {
  margin-top: 16px;
  margin-bottom: 8px;
}

.modal-body h6:first-child {
  margin-top: 0;
}

/* Responsive */
@media (max-width: 1200px) {
  .model-panel {
    width: 180px;
  }
  .stats-panel {
    width: 200px;
  }
}

@media (max-width: 992px) {
  .comparison-content {
    flex-direction: column;
  }
  
  .model-panel,
  .stats-panel {
    width: 100%;
    max-height: 200px;
    border-right: none;
    border-left: none;
    border-bottom: 1px solid #dee2e6;
  }
  
  .model-panel.collapsed,
  .stats-panel.collapsed {
    max-height: 40px;
    width: 100%;
  }
}

/* Ensemble-specific styles */
.ensemble-badge {
  background: #fd7e14 !important;
  font-size: 8px;
}

.ensemble-options {
  padding: 8px;
  margin-top: 8px;
  background: #fff3cd;
  border-radius: 4px;
  border: 1px solid #ffc107;
}

.ensemble-options .form-check {
  margin-bottom: 6px;
}

.ensemble-options .form-check-label {
  font-size: 11px;
}

.opacity-slider {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
}

.opacity-slider input[type="range"] {
  width: 80px;
  height: 4px;
}

/* Ensemble tooltip styles */
.tooltip-content :deep(.tooltip-row.is-ensemble) {
  background: rgba(253, 126, 20, 0.1);
  padding: 2px 4px;
  border-radius: 3px;
  margin: 2px -4px;
}

.tooltip-content :deep(.tooltip-spread) {
  font-size: 10px;
  color: #6c757d;
  display: block;
  margin-top: 1px;
}

/* Ensemble legend indicator */
.legend-item.is-ensemble .legend-color {
  height: 10px;
  border-radius: 3px;
  opacity: 0.6;
}

.legend-item.is-ensemble .legend-name::after {
  content: ' (ensemble)';
  font-size: 10px;
  color: #fd7e14;
}
</style>
